import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  UserCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

export type UserRole = 'client' | 'lawyer';

export interface UserData {
  uid: string;
  email: string;
  role: UserRole;
  fullName: string;
  // Common fields
  phone?: string;
  // Client specific
  nationalId?: string;
  iban?: string;
  jobTitle?: string;
  monthlyIncome?: number;
  monthlyCommitments?: number;
  // Lawyer specific
  licenseNumber?: string;
  commercialRegistryNumber?: string;
  officeLocation?: string;
  licensePhotoUrl?: string;
  registryPhotoUrl?: string;
}

export const signUp = async (
  email: string, 
  password: string, 
  userData: Omit<UserData, 'email' | 'uid'>
): Promise<UserCredential> => {
  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const { user } = userCredential;
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      ...userData,
      email,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    
    return userCredential;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

export const signIn = async (email: string, password: string) => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

export const getCurrentUserData = async (userId: string): Promise<UserData | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { uid: userId, ...userDoc.data() } as UserData;
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};
